/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FeatureCollection } from 'geojson';
import { handleResponse } from '@opengeoweb/api';
import type {
  Concept,
  DataExplorerPresets,
  DiscoveryMetadataFeature,
  DiscoveryMetadataItems,
  Queryable,
} from '../../store/types';

export const LIMIT = 10000;

export const fetchItems = async (
  baseUrl: string,
  search?: string,
  queryables?: Queryable[],
  concepts?: Concept[],
  bbox?: number[],
  meteogateOnly?: boolean,
): Promise<DiscoveryMetadataItems | null> => {
  if (!baseUrl) {
    return null;
  }
  const queryString = buildQueryString(
    search,
    queryables,
    concepts,
    bbox,
    meteogateOnly,
  );
  return fetch(`${baseUrl}/items?limit=${LIMIT}&${queryString}`).then(
    handleResponse,
  ) as Promise<DiscoveryMetadataItems | null>;
};

export const fetchGeoJSON = async (
  collectionUrl: string,
): Promise<FeatureCollection | null> => {
  if (!collectionUrl) {
    return null;
  }
  return fetch(collectionUrl).then(
    handleResponse,
  ) as Promise<FeatureCollection | null>;
};

export const fetchItem = async (
  baseUrl: string,
  datasetId: string,
): Promise<DiscoveryMetadataFeature | null> => {
  if (!baseUrl || !datasetId) {
    return null;
  }
  return fetch(`${baseUrl}/items/${datasetId}`).then(
    handleResponse,
  ) as Promise<DiscoveryMetadataFeature | null>;
};

const buildQueryString = (
  search?: string,
  queryables?: Queryable[],
  concepts?: Concept[],
  bbox?: number[],
  meteogateOnly?: boolean,
): string => {
  const parts: string[] = [];
  if (search !== undefined) {
    parts.push(`q=${search}`);
  }

  if (queryables !== undefined) {
    queryables.forEach((queryable) => {
      if (queryable.selected && queryable.selected.length > 0) {
        parts.push(`${queryable.id}=${queryable.selected.join('|')}`);
      }
    });
  }
  if (concepts !== undefined) {
    const selectedConceptIds = concepts
      .filter((concept) => concept.selected && concept.id !== 'all')
      .map((concept) => concept.id);

    if (selectedConceptIds.length > 0) {
      parts.push(`themes.concepts.id=${selectedConceptIds.join('|')}`);
    }
  }
  if (bbox !== undefined) {
    if (bbox.length === 4) {
      parts.push(`bbox=${bbox.join(',')}`);
    }
  }
  if (meteogateOnly) {
    parts.push('keywords=meteogate');
  }
  return parts.join('&');
};

export const getDataExplorerConfiguration = async (
  productConfigKey: string,
): Promise<DataExplorerPresets> =>
  fetch(`/assets/${productConfigKey}`).then(
    handleResponse,
  ) as Promise<DataExplorerPresets>;
